import './App.css';
import { useRef, useState, useEffect } from 'react';
import { useScroll, useTransform, useInView, motion, AnimatePresence } from 'motion/react';
import HeroSection from './components/HeroSection';
import BioSection from './components/BioSection';
import SocialSection from './components/SocialSection';
import ProjectsSection from './components/ProjectsSection';
import NavBar from './components/NavBar';

const projectThemes = {
  'turtle-race':  '#0d1a0d',
  'rhythm-piano': '#1f0b0d',
  'ripple':       '#0b1230',
  'plain-sight':  '#120a20',
};

const turtleDistance = typeof window !== 'undefined' ? window.innerWidth + 100 : 1600;
const noteDistance = typeof window !== 'undefined' ? window.innerHeight + 100 : 900;

const ripples = [
  { top: '30%', left: '20%', size: 280, duration: 9, delay: 0 },
  { top: '62%', left: '72%', size: 300, duration: 10.5, delay: 2.4 },
  { top: '78%', left: '38%', size: 240, duration: 9.5, delay: 4.6 },
];

// Each origin emits several concentric rings, staggered across its cycle.
const ringsPerRipple = 2;

// Horizontal speed streaks that sweep across for the racing project.
const raceStreaks = [
  { top: '32%', width: 200, thickness: 2, duration: 7.8, delay: 0 },
  { top: '52%', width: 150, thickness: 2, duration: 9.2, delay: 2.8 },
  { top: '70%', width: 230, thickness: 3, duration: 7.0, delay: 4.6 },
];

// Glowing beat dots that pulse as they drop toward the keys — deliberately
// distinct from the racing streaks so the two projects don't feel alike.
const beatDots = [
  { left: '22%', size: 14, duration: 8.8, delay: 0 },
  { left: '52%', size: 12, duration: 9.8, delay: 3.0 },
  { left: '78%', size: 16, duration: 8.2, delay: 5.0 },
];

// Faint binary bits drifting down for the steganography project.
const MONO_FONT = "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace";
const bitStreams = [
  { left: '16%', bits: '01', size: 16, duration: 14, delay: 0 },
  { left: '38%', bits: '10', size: 14, duration: 17, delay: 4 },
  { left: '61%', bits: '011', size: 15, duration: 15, delay: 1.8 },
  { left: '83%', bits: '10', size: 13, duration: 18, delay: 6.2 },
];

function RaceOverlay() {
  return (
    <>
      {raceStreaks.map(({ top, width, thickness, duration, delay }, i) => (
        <motion.div
          key={i}
          style={{
            position: 'fixed',
            top,
            left: -width,
            width,
            height: thickness,
            borderRadius: thickness,
            background: 'linear-gradient(to right, transparent, rgba(120, 220, 140, 0.28), transparent)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
          animate={{ x: turtleDistance + width }}
          transition={{ duration, repeat: Infinity, ease: 'linear', delay }}
        />
      ))}
    </>
  );
}

function BeatOverlay() {
  return (
    <>
      {beatDots.map(({ left, size, duration, delay }, i) => (
        <motion.div
          key={i}
          style={{
            position: 'fixed',
            top: -size,
            left,
            width: size,
            height: size,
            borderRadius: '50%',
            background: 'rgba(255, 120, 130, 0.5)',
            boxShadow: '0 0 16px 3px rgba(255, 90, 110, 0.28)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
          animate={{ y: noteDistance + size, opacity: [0, 1, 1, 0.3] }}
          transition={{
            duration,
            repeat: Infinity,
            ease: 'linear',
            delay,
            times: [0, 0.12, 0.82, 1],
          }}
        />
      ))}
    </>
  );
}

function BitOverlay() {
  return (
    <>
      {bitStreams.map(({ left, bits, size, duration, delay }, i) => (
        <motion.span
          key={i}
          style={{
            position: 'fixed',
            top: -size,
            left,
            fontFamily: MONO_FONT,
            fontSize: size,
            letterSpacing: '2px',
            lineHeight: 1,
            color: 'rgba(190, 170, 255, 0.4)',
            zIndex: 1,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
          animate={{ y: noteDistance + size, opacity: [0, 0.6, 0.6, 0] }}
          transition={{ duration, repeat: Infinity, ease: 'linear', delay, times: [0, 0.1, 0.85, 1] }}
        >
          {bits}
        </motion.span>
      ))}
    </>
  );
}

function RippleOverlay() {
  return (
    <>
      {ripples.flatMap(({ top, left, size, duration, delay }, i) =>
        Array.from({ length: ringsPerRipple }, (_, r) => (
          <motion.div
            key={`${i}-${r}`}
            style={{
              position: 'fixed',
              top,
              left,
              width: size,
              height: size,
              marginTop: -size / 2,
              marginLeft: -size / 2,
              borderRadius: '50%',
              border: '2px solid rgba(120, 170, 255, 0.28)',
              zIndex: 1,
              pointerEvents: 'none',
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 0.2, 1], opacity: [0, 0.6, 0] }}
            transition={{
              duration,
              repeat: Infinity,
              ease: 'easeOut',
              times: [0, 0.15, 1],
              delay: delay + (r * duration) / ringsPerRipple,
            }}
          />
        )),
      )}
    </>
  );
}

const MONO = "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace";

// A hidden reward at the very bottom. It's a normal snapping screen (reached
// after a blank one), so scrolling into it is as smooth as any section. Sits
// on the white background the page fades into, so its text is dark.
function EasterEgg({ sectionRef, style }) {
  return (
    <section
      ref={sectionRef}
      data-snap
      style={{
        height: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        ...style,
      }}
    >
      <motion.code
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 0.7, y: 0 }}
        viewport={{ amount: 0.5, once: true }}
        transition={{ duration: 0.9 }}
        style={{ fontFamily: MONO, fontSize: 'clamp(12px, 3.5vw, 16px)', color: '#111' }}
      >
        {'// the end'}
      </motion.code>
    </section>
  );
}

function App() {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const [activeProject, setActiveProject] = useState(null);

  const heroRef = useRef(null);
  const bioRef = useRef(null);
  const socialRef = useRef(null);
  const thirdRef = useRef(null);
  const eggRef = useRef(null);

  // The whole page fades to white once the ending screen comes into view.
  const eggInView = useInView(eggRef, { amount: 0.4 });

  // Section snapping: commit to the nearest section once you've dragged ~20%
  // toward it; a quick flick carries exactly one section (overriding the
  // browser's momentum) while a slow drag snaps by position. Custom eased glide.
  useEffect(() => {
    const snapPointsOf = () =>
      Array.from(document.querySelectorAll('[data-snap]'))
        .map((el) => el.getBoundingClientRect().top + window.scrollY)
        .sort((a, b) => a - b);
    const nearestIndex = (points, y) => {
      let best = 0;
      let bestDist = Infinity;
      points.forEach((p, k) => {
        const d = Math.abs(p - y);
        if (d < bestDist) { bestDist = d; best = k; }
      });
      return best;
    };

    const THRESHOLD = 0.2;
    const FLICK = 0.35;

    let lastY = window.scrollY;
    let lastT = performance.now();
    let velocity = 0;
    let dir = 'down';
    let touching = false;
    let startIndex = 0;
    let snapping = false;
    let timer;
    let rafId = null;

    const easeOutCubic = (t) => 1 - ((1 - t) ** 3);
    const cancelGlide = () => {
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
      snapping = false;
    };
    const glideTo = (target) => {
      const start = window.scrollY;
      const dist = target - start;
      if (Math.abs(dist) < 2) return;
      cancelGlide();
      window.clearTimeout(timer);
      snapping = true;
      const duration = Math.min(750, Math.max(320, Math.abs(dist) * 0.55));
      const startTime = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - startTime) / duration);
        window.scrollTo(0, start + dist * easeOutCubic(t));
        if (t < 1) { rafId = requestAnimationFrame(step); } else { rafId = null; snapping = false; }
      };
      rafId = requestAnimationFrame(step);
    };

    const decideAndSnap = (useVelocity) => {
      if (snapping) return;
      const points = snapPointsOf();
      if (points.length < 2) return;
      const y = window.scrollY;
      let i = 0;
      while (i < points.length - 1 && points[i + 1] <= y + 1) i += 1;
      const top = points[i];
      const next = points[Math.min(i + 1, points.length - 1)];
      let target;
      if (useVelocity && Math.abs(velocity) >= FLICK) {
        const ti = Math.min(points.length - 1, Math.max(0, startIndex + (dir === 'down' ? 1 : -1)));
        target = points[ti];
      } else if (next !== top) {
        const f = (y - top) / (next - top);
        target = dir === 'down' ? (f >= THRESHOLD ? next : top) : (f <= 1 - THRESHOLD ? top : next);
      } else {
        target = top;
      }
      glideTo(target);
    };

    const onScroll = () => {
      const now = performance.now();
      const y = window.scrollY;
      const dt = now - lastT;
      if (dt > 0) {
        velocity = 0.6 * velocity + 0.4 * ((y - lastY) / dt);
        if (y > lastY) dir = 'down';
        else if (y < lastY) dir = 'up';
      }
      lastY = y;
      lastT = now;
      if (snapping || touching) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => decideAndSnap(false), 55);
    };

    const onTouchStart = () => {
      touching = true;
      window.clearTimeout(timer);
      cancelGlide();
      velocity = 0;
      startIndex = nearestIndex(snapPointsOf(), window.scrollY);
    };
    const onTouchEnd = () => {
      touching = false;
      decideAndSnap(true);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      window.clearTimeout(timer);
      cancelGlide();
    };
  }, []);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth' });

  const backgroundColor = eggInView ? '#ffffff' : (projectThemes[activeProject] ?? '#000000');

  return (
    <div
      className="App"
      style={{ backgroundColor, transition: 'background-color 0.8s ease' }}
    >
      <AnimatePresence>
        {activeProject === 'turtle-race' && (
          <motion.div key="turtles" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
            <RaceOverlay />
          </motion.div>
        )}
        {activeProject === 'rhythm-piano' && (
          <motion.div key="notes" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
            <BeatOverlay />
          </motion.div>
        )}
        {activeProject === 'ripple' && (
          <motion.div key="ripples" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
            <RippleOverlay />
          </motion.div>
        )}
        {activeProject === 'plain-sight' && (
          <motion.div key="bits" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
            <BitOverlay />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content layer — sits above the background animations. */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <NavBar
          opacity={opacity}
          onAbout={() => scrollTo(bioRef)}
          onSocial={() => scrollTo(socialRef)}
          onMore={() => scrollTo(thirdRef)}
        />
        <HeroSection
          heroRef={heroRef}
          onScrollDown={() => scrollTo(bioRef)}
        />
        <BioSection
          bioRef={bioRef}
          onScrollUp={() => scrollTo(heroRef)}
          onScrollDown={() => scrollTo(thirdRef)}
        />
        <ProjectsSection
          thirdRef={thirdRef}
          onScrollUp={() => scrollTo(bioRef)}
          onScrollDown={() => scrollTo(socialRef)}
          onProjectChange={setActiveProject}
        />
        <SocialSection
          socialRef={socialRef}
          onScrollUp={() => scrollTo(thirdRef)}
        />
        {/* A blank screen so the ending stays a scroll-to-find secret. */}
        <div aria-hidden data-snap style={{ height: '100dvh' }} />
        <EasterEgg sectionRef={eggRef} />
        <div aria-hidden style={{ height: 'calc(100lvh - 100svh)' }} />
      </div>
    </div>
  );
}

export default App;
